import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getDashboardData, getDashboardMonths } from '../api/api';
import { formatCurrency } from '../utils/formatters';
import { useCompetence } from '../contexts/CompetenceContext';
import {
    Box, Typography, Paper, Grid, TextField, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Collapse, Chip, CircularProgress, Alert, Stack, Button,
    Tooltip as MuiTooltip, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
    Search as SearchIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowRight as KeyboardArrowRightIcon,
    BarChart as BarChartIcon,
    Sort as SortIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    FileDownload as FileDownloadIcon
} from '@mui/icons-material';

const RelatorioCustoUnitario = () => {
    const { globalCompetence, availableMonths: globalMonths } = useCompetence();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Month selection - use global competence as initial value
    const [availableMonths, setAvailableMonths] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState('');

    // UI States
    const [filterText, setFilterText] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'centro', direction: 'asc' });
    const [expandedRows, setExpandedRows] = useState({});

    const currencyFormatter = formatCurrency;

    // Use global months if available, otherwise fetch
    useEffect(() => {
        if (globalMonths && globalMonths.length > 0) {
            setAvailableMonths(globalMonths);
            if (!selectedMonth && globalCompetence) {
                setSelectedMonth(globalCompetence);
            } else if (!selectedMonth && globalMonths.length > 0) {
                setSelectedMonth(globalMonths[0]);
            }
        } else {
            const fetchMonths = async () => {
                try {
                    const res = await getDashboardMonths();
                    if (res.data && Array.isArray(res.data)) {
                        setAvailableMonths(res.data);
                        if (!selectedMonth && globalCompetence) {
                            setSelectedMonth(globalCompetence);
                        } else if (res.data.length > 0 && !selectedMonth) {
                            setSelectedMonth(res.data[0]);
                        }
                    }
                } catch (err) {
                    console.error("Erro ao carregar meses:", err);
                }
            };
            fetchMonths();
        }
    }, [globalMonths, globalCompetence]);

    // Fetch data when month changes
    const fetchData = useCallback(async () => {
        if (!selectedMonth) return;

        setLoading(true);
        setError(null);
        try {
            const res = await getDashboardData(selectedMonth);
            if (res.data.status === 'Erro') throw new Error(res.data.mensagem);
            setData(res.data);
        } catch (err) {
            setError(err.message || "Erro ao carregar os dados.");
        } finally {
            setLoading(false);
        }
    }, [selectedMonth]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Flatten data for table
    const tableData = useMemo(() => {
        if (!data || !data.custo_unitario_detalhado) return [];

        const rows = [];
        Object.entries(data.custo_unitario_detalhado).forEach(([centro, ccData]) => {
            if (Object.keys(ccData.producoes).length === 0) {
                // Optional: Include CCs with no production if needed
            } else {
                Object.entries(ccData.producoes).forEach(([item, prodData]) => {
                    rows.push({
                        id: `${centro}-${item}`,
                        centro,
                        codigo: prodData.codigo || 'N/A',
                        item,
                        quantidade: prodData.quantidade,
                        peso: prodData.peso_uc || 1.0,
                        custo_unitario: prodData.custo_unitario_total,
                        custo_total: prodData.custo_total_item,
                        detalhe_despesas: prodData.detalhe_despesas
                    });
                });
            }
        });
        return rows;
    }, [data]);

    // Filter and Sort
    const processedData = useMemo(() => {
        let filtered = tableData;

        if (filterText) {
            const lowerFilter = filterText.toLowerCase();
            filtered = filtered.filter(row =>
                row.centro.toLowerCase().includes(lowerFilter) ||
                row.item.toLowerCase().includes(lowerFilter) ||
                (row.codigo && row.codigo.toLowerCase().includes(lowerFilter))
            );
        }

        if (sortConfig.key) {
            filtered.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filtered;
    }, [tableData, filterText, sortConfig]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const toggleRow = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) return <SortIcon fontSize="small" sx={{ color: 'text.disabled', ml: 0.5 }} />;
        return sortConfig.direction === 'asc'
            ? <ArrowUpwardIcon fontSize="small" sx={{ color: 'primary.main', ml: 0.5 }} />
            : <ArrowDownwardIcon fontSize="small" sx={{ color: 'primary.main', ml: 0.5 }} />;
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    );

    if (error) return (
        <Box sx={{ p: 3 }}>
            <Alert severity="error">Erro: {error}</Alert>
        </Box>
    );

    if (!data || !data.custo_unitario_detalhado) return (
        <Box sx={{ p: 3 }}>
            <Alert severity="info">Nenhum dado disponível.</Alert>
        </Box>
    );

    // Export handlers
    const handleExportCSV = () => {
        if (processedData.length === 0) return;

        const columns = [
            { key: 'centro', label: 'Centro de Custo' },
            { key: 'codigo', label: 'Código' },
            { key: 'item', label: 'Item de Produção' },
            { key: 'quantidade', label: 'Quantidade' },
            { key: 'peso', label: 'Peso' },
            { key: 'custo_unitario', label: 'Custo Unitário' },
            { key: 'custo_total', label: 'Custo Total' }
        ];

        const headers = columns.map(c => c.label).join(';');
        const rows = processedData.map(row =>
            columns.map(c => {
                const val = row[c.key];
                return typeof val === 'number'
                    ? val.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                    : val || '';
            }).join(';')
        );
        const csv = '\uFEFF' + [headers, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `custo_unitario_${data.mes || 'atual'}.csv`;
        link.click();
    };

    const handlePrint = () => window.print();

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                        <BarChartIcon sx={{ mr: 2, color: 'primary.main' }} /> Relatório de Custo Unitário
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Análise detalhada do custo unitário por item de produção.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <MuiTooltip title="Exportar para CSV">
                        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportCSV} sx={{ textTransform: 'none' }}>
                            CSV
                        </Button>
                    </MuiTooltip>
                    <MuiTooltip title="Imprimir">
                        <Button variant="outlined" onClick={handlePrint} sx={{ textTransform: 'none' }}>
                            Imprimir
                        </Button>
                    </MuiTooltip>
                </Stack>
            </Stack>

            {/* Controls */}
            <Paper sx={{ p: 2, mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="month-select-label">Competência</InputLabel>
                        <Select
                            labelId="month-select-label"
                            value={selectedMonth}
                            label="Competência"
                            onChange={(e) => setSelectedMonth(e.target.value)}
                        >
                            {availableMonths.map((month) => (
                                <MenuItem key={month} value={month}>
                                    {month}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        placeholder="Filtrar por Centro, Código ou Item..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{ width: '350px' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    Mostrando <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{processedData.length}</Box> itens
                </Typography>
            </Paper>

            {/* Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Table stickyHeader size="medium">
                    <TableHead>
                        <TableRow>
                            <TableCell width="50px" />
                            <TableCell
                                onClick={() => requestSort('centro')}
                                sx={{ cursor: 'pointer', fontWeight: 'bold', '&:hover': { bgcolor: 'action.hover' } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    Centro de Custo {getSortIcon('centro')}
                                </Box>
                            </TableCell>
                            <TableCell
                                onClick={() => requestSort('codigo')}
                                sx={{ cursor: 'pointer', fontWeight: 'bold', '&:hover': { bgcolor: 'action.hover' } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    Código {getSortIcon('codigo')}
                                </Box>
                            </TableCell>
                            <TableCell
                                onClick={() => requestSort('item')}
                                sx={{ cursor: 'pointer', fontWeight: 'bold', '&:hover': { bgcolor: 'action.hover' } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    Item de Produção {getSortIcon('item')}
                                </Box>
                            </TableCell>
                            <TableCell
                                align="right"
                                onClick={() => requestSort('quantidade')}
                                sx={{ cursor: 'pointer', fontWeight: 'bold', '&:hover': { bgcolor: 'action.hover' } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    Qtd. {getSortIcon('quantidade')}
                                </Box>
                            </TableCell>
                            <TableCell
                                align="right"
                                onClick={() => requestSort('peso')}
                                sx={{ cursor: 'pointer', fontWeight: 'bold', '&:hover': { bgcolor: 'action.hover' } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    Peso {getSortIcon('peso')}
                                </Box>
                            </TableCell>
                            <TableCell
                                align="right"
                                onClick={() => requestSort('custo_unitario')}
                                sx={{ cursor: 'pointer', fontWeight: 'bold', '&:hover': { bgcolor: 'action.hover' } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    Custo Unit. {getSortIcon('custo_unitario')}
                                </Box>
                            </TableCell>
                            <TableCell
                                align="right"
                                onClick={() => requestSort('custo_total')}
                                sx={{ cursor: 'pointer', fontWeight: 'bold', '&:hover': { bgcolor: 'action.hover' } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    Custo Total {getSortIcon('custo_total')}
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {processedData.map((row) => (
                            <React.Fragment key={row.id}>
                                <TableRow
                                    hover
                                    onClick={() => toggleRow(row.id)}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: expandedRows[row.id] ? 'action.selected' : 'inherit',
                                        '& > *': { borderBottom: 'unset' }
                                    }}
                                >
                                    <TableCell>
                                        <IconButton aria-label="expand row" size="small" onClick={(e) => { e.stopPropagation(); toggleRow(row.id); }}>
                                            {expandedRows[row.id] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}>{row.centro}</TableCell>
                                    <TableCell>
                                        <Chip label={row.codigo} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>{row.item}</TableCell>
                                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{row.quantidade}</TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={`${row.peso}x`}
                                            size="small"
                                            color={row.peso > 1 ? "warning" : "default"}
                                            variant={row.peso > 1 ? "filled" : "outlined"}
                                            sx={{ height: 24, fontSize: '0.75rem' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'success.main' }}>
                                        {currencyFormatter(row.custo_unitario)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                        {currencyFormatter(row.custo_total)}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                        <Collapse in={expandedRows[row.id]} timeout="auto" unmountOnExit>
                                            <Box sx={{ margin: 2 }}>
                                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                                                    <Typography variant="subtitle2" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>
                                                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mr: 1 }} />
                                                        Composição do Custo Unitário
                                                    </Typography>
                                                    <Table size="small" aria-label="purchases">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Despesa</TableCell>
                                                                <TableCell align="right">Valor Total no Item</TableCell>
                                                                <TableCell align="right">Custo Unitário</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {row.detalhe_despesas.map((despesa, idx) => (
                                                                <TableRow key={idx} hover>
                                                                    <TableCell component="th" scope="row">
                                                                        {despesa.descricao}
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                                                        {currencyFormatter(despesa.valor_total_cc)}
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                                                        {currencyFormatter(despesa.custo_unitario_despesa)}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </Paper>
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                        {processedData.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                    <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary">Nenhum item encontrado</Typography>
                                    <Typography variant="body2" color="text.disabled">Tente ajustar os filtros de busca.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default RelatorioCustoUnitario;
